import AddIcon from "@mui/icons-material/Add";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import CachedIcon from "@mui/icons-material/Cached";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import ConstructionIcon from "@mui/icons-material/Construction";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import InfoIcon from "@mui/icons-material/Info";
import ManageAccountsOutlinedIcon from "@mui/icons-material/ManageAccountsOutlined";
import OutlinedFlagOutlinedIcon from "@mui/icons-material/OutlinedFlagOutlined";
import PlaceIcon from "@mui/icons-material/Place";
import RemoveRedEyeOutlinedIcon from "@mui/icons-material/RemoveRedEyeOutlined";
import ReplayIcon from "@mui/icons-material/Replay";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import TroubleshootIcon from "@mui/icons-material/Troubleshoot";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";
import { Box, Button, Grid2, Link, MenuItem, Select, SelectChangeEvent, Typography } from "@mui/material";
import { GridActionsCellItem, GridColDef, GridRowParams, GridRowsProp, useGridApiRef } from "@mui/x-data-grid-premium";
import { produce } from "immer";
import moment from "moment";
import { FC, useContext, useEffect, useMemo, useState, ReactNode } from "react";
import { Link as RouterLink, useLocation } from "react-router";
import { EventNotifierOperation } from "../../../Core/Api/notifier-client";
import { cacheService, cockpitService, eventsNotifier, workflowService } from "../../../Core/Api/services";
import { AclTypes } from "../../../Core/Api/Types/acl-types";
import { Action } from "../../../Core/Api/Types/cockpit-action";
import { ResourceType } from "../../../Core/Api/Types/cockpit-ressource-type";
import { Licences } from "../../../Core/Api/Types/licences";
import { MakeOperationRequest } from "../../../Core/Api/Types/make-operation-request";
import { ObjectClass } from "../../../Core/Api/Types/object-class";
import { ObjectType } from "../../../Core/Api/Types/object-type";
import { PlatformNames } from "../../../Core/Api/Types/platform-names";
import { WorkflowProps } from "../../../Core/Api/Workflow/workflow-api-service";
import { GetMetadataByTagName } from "../../../Core/Utils/asset-object-utils";
import GlobalConst from "../../../Core/Utils/Constants/globals";
import { IngestStatus } from "../../../Pages/admin/Ingest/IngestIndex/IngestProcessing/ingest-processing";
import { routePaths } from "../../../Router/router";
import {
  AclMatch,
  Distinct,
  GetEnumValueLabelMap,
  OpenLinkInNewTab,
  UserHasAcl,
  UserHasAcls,
  UserHasLicence,
} from "../../../Utils/helpers";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import AssetTypeFromObjectChip from "../../Misc/Chips/AssetChip/asset-type-chip-from-object";
import PageFilter from "../../Misc/PageFilter";
import { ModalContext } from "../../Modals";
import ModalOrderFileFixingAction, {
  ModalOrderFileFixingActionElementProps,
} from "../../Modals/ModalOrderFileFixing/modal-order-file-fixing-action";
import ModalSetFollowers, { ModalSetFollowersElementProps } from "../../Modals/ModalSetFollowers/modal-set-followers";
import ModalWorkflowDetails, {
  ModalWorkflowDetailsElementProps,
} from "../../Modals/ModalShowDetails/modal-workflow-details";
import AssetPanel from "../../Panels/AssetPanel/asset-panel";
import NewRequestPanel from "../../Panels/NewRequestPanels/new-request-panel";
import TableCellActionReportIssue from "../TableCellActionReportIssue/table-cell-action-report-issue";
import DataTable, { CellSize } from "../table";
import TableCellDateTime from "../TableCellDateTime/table-cell-date-time";
import TableCellIngestPriorityChip from "../TableCellIngestPriorityChip/table-cell-ingest-priority-chip";
import TableCellIngestProgressBar from "../TableCellIngestProgressBar/table-cell-ingest-progress-bar";
import TableCellIngestStatus from "../TableCellIngestStatus/table-cell-ingest-status";
import TableCellMovie from "../TableCellMovie/table-cell-movie";
import TableCellStringExpend from "../TableCellStringExpend/table-cell-string-expend";
import TableCellTypography, { TableCellLongTypography } from "../TableCellTypography/table-cell-typography";
import { PlatformMode } from "../../../Core/Api/Types/platform-mode";
import TableCell2Lines from "../TableCell2Lines/table-cell-2-lines";
import { useTranslation } from "react-i18next";
import { TokenOutlined, VerifiedOutlined } from "@mui/icons-material";
import TableUuids from "../table-uuids";
import { DbWorkflowInstanceInfos } from "../../../Core/Api/Types/db-workflow-instance-infos";
import { WorkflowTag } from "../../../Core/Api/Types/workflow-tag";

export enum IngestWorkflowTableContext {
  Normal,
  Quotes,
  Search,
}
interface IngestWorkflowTableProps {
  isLoading: boolean;
  workflows: GridRowsProp;
  filters: WorkflowProps["filtersData"];
  instancesNumber: WorkflowProps["countInstances"];
  platforms?: PlatformNames[];
  isCockpit?: boolean;
  onFilteredItemCountChange?: (count: number) => void;
}

const IngestWorkflowTable: FC<IngestWorkflowTableProps> = (props) => {
  const { t } = useTranslation();
  const {
    workflows,
    filters,
    instancesNumber,
    platforms,
    isCockpit = false,
    onFilteredItemCountChange,
    isLoading,
  } = props;

  // context
  const modalContext = useContext(ModalContext);
  const { openModal } = { ...modalContext };

  const location = useLocation();
  // #region States
  // Top filters
  const [filtersData, setFiltersData] = useState<WorkflowProps["filtersData"]>({
    createdByNames: [],
    providersNames: [],
  });
  const [createdByFilter, setCreatedByFilter] = useState("");
  const [providerFilter, setProviderFilter] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("");
  const [textFilter, setTextFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState<ObjectType | "">("");
  const [classFilter, setClassFilter] = useState<ObjectClass | "">("");
  const [episodesFilter, setEpisodesFilter] = useState<number[]>([]);
  const [filterPlatform, setFilterPlatform] = useState<number>(-1);

  // Box filters
  const [countInstances, setCountInstances] = useState<WorkflowProps["countInstances"]>({
    countInstancesAccepted: 0,
    countInstancesProcessing: 0,
    countInstancesPendingQC: 0,
    countInstancesRejected: 0,
    countInstancesError: 0,
    countInstancesWaitingMezz: 0,
    countInstancesQcInProgress: 0,
    countInstancesMissingMetadata: 0,
  });
  const [statusFilter, setStatusFilter] = useState<IngestStatus>();

  // Table rows
  const [rows, setRows] = useState<GridRowsProp>([]);

  const isDrive = cacheService.GetPlatformMode() === PlatformMode.Browser;
  const xsValue = isDrive ? 12 / 5 : 12 / 7;
  // Asset and Request panels
  const [openAssetPanel, setOpenAssetPanel] = useState(false);
  const [openNewRequestPanel, setOpenNewRequestPanel] = useState(false);
  const aclRetry = UserHasAcls([AclTypes.aclSuperUser, AclTypes.aclAdmin], AclMatch.OneAtLeast);
  const aclPriority = UserHasAcl(AclTypes.aclIngestAssignPriority);
  const aclFollowers = UserHasAcl(AclTypes.aclWorkflowAssignFollowers);
  const aclAdmin = UserHasAcl(AclTypes.aclAdmin);
  const aclSuperUser = UserHasAcl(AclTypes.aclSuperUser);
  const apiGrid = useGridApiRef();
  const platfomMode = cacheService.GetPlatformMode();

  // #endregion

  // #region Refs and custom hooks
  const [selectedAssetId, setSelectedAssetId] = useState<number | undefined>();
  const enqueueCustomSnackbar = useCustomSnackbar();
  // #endregion

  // #region Functions
  const handleChangeCreatedBySelect = (event: SelectChangeEvent) => {
    const createdByFilterValue = event.target.value as string;
    setCreatedByFilter(createdByFilterValue);
  };

  const handleChangeProviderSelect = (event: SelectChangeEvent) => {
    const providerFilterValue = event.target.value as string;
    setProviderFilter(providerFilterValue);
  };

  const handleChangePrioritySelect = (event: SelectChangeEvent) => {
    const priorityFilterValue = event.target.value as string;
    setPriorityFilter(priorityFilterValue);
  };

  // Box filters
  const changeStatusFilter = (statuses: IngestStatus) => {
    // if click on same filter
    if (statuses === statusFilter) {
      setStatusFilter(undefined);
      return;
    }

    setStatusFilter(statuses);
  };

  const handleFilterType = (event: SelectChangeEvent) => {
    const value = event.target.value as string;
    if (value === "") setTypeFilter("");
    else setTypeFilter(ObjectType[value]);
  };

  const handleFilterClass = (event: SelectChangeEvent) => {
    const value = event.target.value as string;
    if (value === "") setClassFilter("");
    else setClassFilter(ObjectClass[value]);
  };

  const handleFilterEpisodes = (event: SelectChangeEvent<typeof episodesFilter>) => {
    const {
      target: { value },
    } = event;
    setEpisodesFilter(
      // On autofill we get a stringified value.
      typeof value === "string" ? [] : value
    );
  };

  const handleFilterPlatform = (event: SelectChangeEvent<number>) => {
    setFilterPlatform(event.target.value as number);
  };

  // Asset and New Request panels
  const hideAssetPanel = () => {
    setOpenAssetPanel(false);
  };

  const showAssetPanel = (e: GridRowParams) => {
    if (!isCockpit) {
      setSelectedAssetId(e.row["assetId"]);
      setOpenAssetPanel(true);
    }
  };

  const hideNewRequestPanel = () => {
    setOpenNewRequestPanel(false);
  };

  const showNewRequestPanel = () => {
    setOpenNewRequestPanel(true);
  };

  // Row buttons
  const handleSetFollowers = (assetId: number, actualFollowersIds: number[]) => {
    if (!openModal) return;

    openModal<ModalSetFollowersElementProps>(ModalSetFollowers, {
      objectIds: [assetId],
      objectType: "asset",
      currentFollowersIds: actualFollowersIds,
    });
  };

  const handleSetPriority = (instanceId: number, priority: number) => {
    workflowService.SetPriorityOnInstanceById(instanceId, priority).then((response) => {
      if (response.success) enqueueCustomSnackbar(t("alert:success.prioritySetOnWorkflow"), "success");
      else enqueueCustomSnackbar(t("alert:error.oopsWithMessage", { messegae: response.message }), "error");
    });
  };

  const handleRetry = (instanceId: number) => {
    isCockpit
      ? cockpitService
          .MakeAdminOperation(
            new MakeOperationRequest({
              resourceId: instanceId,
              resourceType: ResourceType.OrderItem,
              action: Action.Retry,
            })
          )
          .then((response) => {
            if (response) enqueueCustomSnackbar(t("alert:success.workflowRetrying"), "success");
          })
      : workflowService.RetryInstance(instanceId).then(() => {
          enqueueCustomSnackbar(t("alert:success.workflowRetrying"), "success");
        });
  };

  const handleAskFix = (objectId: number) => {
    if (!openModal) {
      return;
    }

    openModal<ModalOrderFileFixingActionElementProps>(ModalOrderFileFixingAction, {
      objectId: objectId,
      onConfirm: () => {},
    });
  };
  const plaformMode = cacheService.GetPlatformMode();
  // #endregion

  // #region Grid definition
  let _rows: GridRowsProp = [];
  type Row = (typeof _rows)[number];

  const adamCols = location.pathname.includes("sources")
    ? {
        field: "rc_prg_media_id",
        headerName: "Id Prg.",
        flex: CellSize.small,
        minWidth: CellSize.small,
        valueGetter: (data: any, row: any) =>
          row?.asset ? GetMetadataByTagName(row?.asset, "rc_prg_media_id")?.value : "",
        renderCell: (data: any) => <Typography variant="body2">{data.value}</Typography>,
      }
    : {
        field: "rc_ba_id",
        headerName: "Id Ba",
        flex: CellSize.medium,
        minWidth: CellSize.medium,
        valueGetter: (data: any, row: any) => (row?.asset ? GetMetadataByTagName(row?.asset, "rc_ba_id")?.value : ""),
        renderCell: (data: any) => <Typography variant="body2">{data.value}</Typography>,
      };

  const ingestProcessingColumns: GridColDef[] = useMemo(
    () => [
      {
        field: "platformId",
        headerName: t("common:platform"),
        minWidth: CellSize.small,
        flex: CellSize.small,
        renderCell: (data: Row) =>
          platforms && platforms!.find((x) => x.id === data.value)
            ? platforms!.find((x) => x.id === data.value)!.name
            : "",
      },
      {
        headerName: t("menu:item.objectId"),
        field: "assetId",
        minWidth: CellSize.small,
        flex: CellSize.small,
        renderCell: (data: Row) => (
          <Link
            color="primary"
            underline="hover"
            component={RouterLink}
            target="_blank"
            onClick={(e) => e.stopPropagation()}
            to={`${routePaths.asset.path}/${data.value}`}
          >
            {data.value}
          </Link>
        ),
      },
      {
        headerName: t("common:title"),
        field: "assetParentTitleId",
        minWidth: CellSize.large,
        flex: CellSize.large,
        valueGetter: (params: any, row: any) => {
          if (!row) return;
          return `${row?.assetParentTitleName} ${row?.assetParentTitleId}`;
        },
        valueFormatter: (params: any, row: any) => {
          if (params) {
            return `${row.assetParentTitleName + " " || ""}#${row.assetParentTitleId || ""}`;
          } else return "";
        },
        renderCell: (data: Row) => (
          <TableCellMovie
            id={data?.row?.assetParentTitleId ?? data?.row?.assetId}
            name={data.row["assetParentTitleName"]}
            isDrive={data?.row?.assetParentTitleId === null}
          />
        ),
      },
      {
        headerName: t("table:headerName.contentAndAsset"),
        field: "assetName",
        minWidth: CellSize.xlarge,
        flex: CellSize.xlarge,
        renderCell: (data: Row) => (
          <TableCell2Lines title={data?.row?.assetParentObjectName} desc={data?.row?.assetName} />
        ),
      },
      {
        headerName: t("common:type"),
        field: "assetType",
        minWidth: CellSize.small,
        flex: CellSize.small,
        type: "singleSelect",
        valueOptions: GetEnumValueLabelMap(ObjectType),
        renderCell: (data: Row) => <AssetTypeFromObjectChip size="small" obj={data?.row?.asset} />,
      },
      {
        headerName: t("common:createdBy"),
        field: "createdByUserName",
        minWidth: CellSize.small,
        flex: CellSize.small,
        renderCell: (data: Row) => <TableCellTypography color={"text.secondary"} fontWeight={500} data={data.value} />,
      },
      {
        headerName: t("common:startDateLowerCase"),
        field: "createdAt",
        minWidth: CellSize.small,
        flex: CellSize.small,
        type: "date",
        valueGetter: (_, row: Row) => (row.createdAt === null ? null : new Date(row.createdAt!)),
        valueFormatter: (params: any, row: any) => {
          if (params)
            return moment(params.toString()).format(
              platfomMode !== PlatformMode.Adam
                ? GlobalConst.MOMENT_DATE_FORMAT_DATENOHOURS
                : GlobalConst.MOMENT_DATE_FORMAT_DATENOHOURS_FR
            );
          else return "";
        },
        renderCell: (data: Row) => (
          <TableCellDateTime
            data={{ ...data, value: data.row?.createdAt }}
            format={
              platfomMode !== PlatformMode.Adam
                ? GlobalConst.MOMENT_DATE_FORMAT_DATENOHOURS
                : GlobalConst.MOMENT_DATE_FORMAT_DATENOHOURS_FR
            }
          />
        ),
      },
      {
        headerName: t("common:endDateLowerCase"),
        field: "endTime",
        minWidth: CellSize.small,
        flex: CellSize.small,
        type: "date",
        valueGetter: (_, row: Row) => (row.endTime === null ? null : new Date(row.endTime!)),
        valueFormatter: (params: any, row: any) => {
          if (params)
            return moment(params.toString()).format(
              platfomMode !== PlatformMode.Adam
                ? GlobalConst.MOMENT_DATE_FORMAT_DATENOHOURS
                : GlobalConst.MOMENT_DATE_FORMAT_DATENOHOURS_FR
            );
          else return "";
        },
        renderCell: (data: Row) => (
          <TableCellDateTime
            data={{ ...data, value: data.row?.endTime }}
            format={
              platfomMode !== PlatformMode.Adam
                ? GlobalConst.MOMENT_DATE_FORMAT_DATENOHOURS
                : GlobalConst.MOMENT_DATE_FORMAT_DATENOHOURS_FR
            }
          />
        ),
      },
      {
        headerName: t("common:provider"),
        field: "providerName",
        minWidth: CellSize.small,
        flex: CellSize.small,
        renderCell: (data: Row) => <TableCellStringExpend data={data} />,
      },
      {
        headerName: t("common:status"),
        field: "status",
        minWidth: CellSize.medium,
        flex: CellSize.medium,
        type: "singleSelect",
        valueOptions: Object.keys(IngestStatus).map((x) => IngestStatus[x]),
        valueFormatter: (params: any, row: any) => {
          return params ?? "";
        },
        renderCell: (data: Row) => <TableCellIngestStatus data={data} />,
      },
      {
        headerName: t("common:priority"),
        field: "priority",
        minWidth: CellSize.xsmall,
        flex: CellSize.xsmall,
        type: "singleSelect",
        valueOptions: ["Normal", "High", "Critical"],
        valueGetter: (data: Row, row: any) => {
          if (!row) return;
          if (row?.priority) {
            if (row?.priority <= 50) {
              return "Normal";
            } else if (row?.priority > 50 && row?.priority < 75) {
              return "High";
            } else {
              return "Critical";
            }
          } else return "";
        },
        renderCell: (data: Row) => <TableCellIngestPriorityChip data={{ ...data, value: data?.row?.priority }} />,
      },
      {
        headerName: t("common:progress"),
        field: "progress",
        type: "number",
        align: "left",
        headerAlign: "left",
        minWidth: CellSize.medium,
        flex: CellSize.medium,
        valueFormatter: (params: any, row: any) => {
          if (params) return `${params}%`;
          else return "0%";
        },
        renderCell: (data: Row) => <TableCellIngestProgressBar data={data} />,
      },
      {
        headerName: t("common:annotation"),
        field: "annotation",
        minWidth: CellSize.large,
        flex: CellSize.large,
        renderCell: (data: Row) => <TableCellLongTypography data={data.row?.annotation} color={"text.secondary"} />,
      },

      {
        headerName: "",
        field: "actions",
        type: "actions",
        align: "right",
        minWidth: CellSize.actionsSmall,
        flex: CellSize.actionsSmall,
        getActions: (params: GridRowParams) => {
          if (!params || !params.row) return;
          const priority = params.row["priority"];

          const setPriorityHighButton = (
            <GridActionsCellItem
              icon={<ArrowUpwardIcon />}
              label={t("table:cellLabel.setPriorityHigh")}
              onClick={() => handleSetPriority(params?.row?.id, 60)}
              showInMenu
              disabled={!aclPriority}
            />
          );
          const setPriorityNormalButton = (
            <GridActionsCellItem
              icon={<ArrowForwardIcon />}
              label={t("table:cellLabel.setPriorityNormal")}
              onClick={() => handleSetPriority(params?.row?.id, 50)}
              showInMenu
              disabled={!aclPriority}
            />
          );
          const setPriorityLowButton = (
            <GridActionsCellItem
              icon={<ArrowDownwardIcon />}
              label={t("table:cellLabel.setPriorityLow")}
              onClick={() => handleSetPriority(params?.row?.id, 20)}
              showInMenu
              disabled={!aclPriority}
            />
          );
          const setPriorityCriticalButton = (
            <GridActionsCellItem
              icon={<PriorityHighIcon />}
              label={t("table:cellLabel.setPriorityCritical")}
              onClick={() => handleSetPriority(params?.row?.id, 75)}
              showInMenu
              disabled={!aclPriority}
            />
          );

          let buttons: ReactNode[] = [];
          if (!isCockpit) {
            buttons = [
              <GridActionsCellItem
                icon={<InfoIcon sx={{ width: 20, height: 20, color: "text.primary" }} />}
                label={t("common:infos")}
                onClick={() => {
                  workflowService.GetInstanceById(params?.row?.id).then((response) => {
                    if (openModal)
                      openModal<ModalWorkflowDetailsElementProps>(ModalWorkflowDetails, {
                        workflow: response.instance?.workflow!,
                      });
                  });
                }}
                disabled={!aclRetry}
                showInMenu
              />,
              <GridActionsCellItem
                icon={<TroubleshootIcon sx={{ width: 20, height: 20, color: "primary.text" }} />}
                label={t("common:qcTool")}
                onClick={() => {
                  OpenLinkInNewTab(`${routePaths.toolsQc.path}/${params?.row?.assetId}`);
                }}
                showInMenu={!aclAdmin && !aclSuperUser}
              />,
              <GridActionsCellItem
                icon={<GroupsOutlinedIcon />}
                label={t("common:setFollowers")}
                onClick={() => handleSetFollowers(params.row["assetId"], params.row["followersIds"])}
                showInMenu
                disabled={!aclFollowers}
              />,
            ];
          }

          if (!isCockpit)
            if (priority > 20 && priority <= 50) {
              buttons.push(setPriorityLowButton, setPriorityHighButton, setPriorityCriticalButton);
            } else if (priority > 50 && priority < 75) {
              buttons.push(setPriorityLowButton, setPriorityNormalButton, setPriorityCriticalButton);
            } else {
              buttons.push(setPriorityLowButton, setPriorityNormalButton, setPriorityHighButton);
            }

          if (params?.row?.status === "Workflow Error" && aclRetry)
            buttons.push(
              <GridActionsCellItem
                icon={<ReplayIcon color="warning" />}
                label={t("common:retry")}
                onClick={() => handleRetry(params?.row?.id)}
                showInMenu
              />
            );

          if (params?.row?.status === "Rejected" && UserHasLicence(Licences.QuoteManagement) && !isCockpit)
            buttons.push(
              <GridActionsCellItem
                icon={<ConstructionIcon color="primary" />}
                label={t("common:requestFileFixing")}
                onClick={() => handleAskFix(params?.row?.assetId)}
                showInMenu
              />
            );

          // id for the tutorial
          buttons.push(<Box id="tutorial-ingest-processing-menu"></Box>);

          if (UserHasAcl(AclTypes.aclSupportTicketCreate)) {
            let platformSettings = cacheService.GetPlatformSettings();
            buttons.push(
              <TableCellActionReportIssue
                text={`
                    ${t("common:platform")}: ${platformSettings?.name}\n
                    ${t("common:assetIdLowerCase")}: ${params?.row?.assetId}\n
                    ${t("common:assetName")}: ${params?.row?.assetName}\n
                    ${t("common:status")}: ${params?.row?.status}\n
                    ${t("common:progression")}: ${params?.row?.progress}\n
                    ${t("common:providerName")}: ${params?.row?.providerName}
                  `}
                typology="Ingest"
                objectId={params?.row?.assetId}
                showInMenu
                {...params}
              />
            );
          }

          return buttons;
        },
      },
    ],
    [handleSetPriority]
  );
  // #endregion

  const filteredRows = useMemo(() => {
    const output = rows.filter(
      (x) =>
        x.status !== IngestStatus.Unknown &&
        (textFilter === "" ||
          x.assetName?.toString().includesAllWords(textFilter) ||
          x.assetId?.toString().includesAllWords(textFilter) ||
          x.assetParentTitleName?.toString().includesAllWords(textFilter) ||
          GetMetadataByTagName(x.asset, "rc_prg_media_id")?.value.includesAllWords(textFilter) ||
          GetMetadataByTagName(x.asset, "rc_ba_id")?.value.includesAllWords(textFilter)) &&
        (filterPlatform === -1 || x.platformId === filterPlatform) &&
        (!statusFilter ||
          x.status === statusFilter ||
          (statusFilter === IngestStatus.WorkflowError &&
            (x.status === IngestStatus.WorkflowError || x.status === IngestStatus.Aborted)) ||
          (statusFilter === IngestStatus.Rejected &&
            (x.status === IngestStatus.Rejected || x.status === IngestStatus.RejectedAuto)) ||
          (statusFilter === IngestStatus.Running &&
            (x.status === IngestStatus.Running || x.status === IngestStatus.QcInProgress))) &&
        (createdByFilter === "" || x.createdByUserName === createdByFilter) &&
        (providerFilter === "" || x.providerName === providerFilter) &&
        (classFilter === "" || x.assetClass === classFilter) &&
        (typeFilter === "" || x.assetType === typeFilter) &&
        (priorityFilter === "" ||
          (x.priority === 50 && priorityFilter === "Normal") ||
          (x.priority > 50 && priorityFilter === "High") ||
          (x.priority < 50 && priorityFilter === "Low")) &&
        (episodesFilter.length === 0 ||
          episodesFilter.includes(GetMetadataByTagName(x.asset, "serieEpisodeNumberProduction")?.value))
    );

    onFilteredItemCountChange && onFilteredItemCountChange(output.length);

    return output;
  }, [
    rows,
    createdByFilter,
    providerFilter,
    priorityFilter,
    textFilter,
    typeFilter,
    classFilter,
    episodesFilter,
    filterPlatform,
    statusFilter,
  ]);

  //------Hooks------
  // initial useEffect
  useEffect(() => {
    if (!workflows) return;
    setRows(workflows);
    setFiltersData(filters);
    setCountInstances(instancesNumber);
  }, [workflows]);

  // SignalR
  useEffect(() => {
    eventsNotifier.Connect().then(function (result) {
      console.debug("Hub events connected successfully to WebSocket and is listening to WorkflowInstanceMessage!");

      if (eventsNotifier.connection)
        eventsNotifier.connection.on("WorkflowInstanceMessage", (data) => {
          if (location.pathname === routePaths.ingestAdamSources.path) {
            const item = data.data as DbWorkflowInstanceInfos;
            if (!item.tags?.includes(WorkflowTag.AdamSource)) return;
          }
          if (location.pathname === routePaths.ingestAdamTrailers.path) {
            const item = data.data as DbWorkflowInstanceInfos;
            if (!item.tags?.includes(WorkflowTag.AdamTrailer)) return;
          }
          switch (data.op) {
            case EventNotifierOperation.Create:
              let instanceComputedForCreation = workflowService.GetWorkflowsComputed(data.data);
              // add row to table
              setRows(
                produce((draft) => {
                  draft.push(instanceComputedForCreation);
                })
              );
              // update filtersData
              setFiltersData(
                produce((draft) => {
                  if (!draft.createdByNames.includes(instanceComputedForCreation.createdByUserName))
                    draft.createdByNames.push(instanceComputedForCreation.createdByUserName);
                  if (!draft.providersNames.includes(instanceComputedForCreation.providerName))
                    draft.providersNames.push(instanceComputedForCreation.providerName);
                })
              );
              setCountInstances(
                produce((draft) => {
                  switch (instanceComputedForCreation.status) {
                    case IngestStatus.Accepted:
                      draft.countInstancesAccepted++;
                      break;
                    case IngestStatus.Running:
                      draft.countInstancesProcessing++;
                      break;
                    case IngestStatus.PendingQC:
                      draft.countInstancesPendingQC++;
                      break;
                    case IngestStatus.Rejected:
                      draft.countInstancesRejected++;
                      break;
                    case IngestStatus.WorkflowError:
                      draft.countInstancesError++;
                      break;
                    case IngestStatus.WaitingMezz:
                      draft.countInstancesWaitingMezz++;
                      break;
                    case IngestStatus.QcInProgress:
                      draft.countInstancesQcInProgress++;
                      break;
                    case IngestStatus.MissingMetadata:
                      draft.countInstancesMissingMetadata++;
                      break;
                  }
                })
              );
              break;
            case EventNotifierOperation.Update:
              let instanceComputedForUpdate = workflowService.GetWorkflowsComputed(data.data);
              // update rows
              setRows(
                produce((draft) => {
                  const index = draft.findIndex((x) => x.id === instanceComputedForUpdate.id);
                  if (index !== -1) draft[index] = instanceComputedForUpdate;
                })
              );
              // update filtersData
              setFiltersData(
                produce((draft) => {
                  if (!draft.createdByNames.includes(instanceComputedForUpdate.createdByUserName))
                    draft.createdByNames.push(instanceComputedForUpdate.createdByUserName);
                  if (!draft.providersNames.includes(instanceComputedForUpdate.providerName))
                    draft.providersNames.push(instanceComputedForUpdate.providerName);
                })
              );
              //TODO : update CountInstances
              break;
            case EventNotifierOperation.Delete:
              let instanceComputedForDeletion = workflowService.GetWorkflowsComputed(data.data);
              // delete row from table
              setRows(
                produce((draft) => {
                  const index = draft.findIndex((x) => x.id === data.data.id);
                  if (index !== -1) draft.splice(index, 1);
                })
              );
              setCountInstances(
                produce((draft) => {
                  switch (instanceComputedForDeletion.status) {
                    case IngestStatus.Accepted:
                      draft.countInstancesAccepted--;
                      break;
                    case IngestStatus.Running:
                      draft.countInstancesProcessing--;
                      break;
                    case IngestStatus.PendingQC:
                      draft.countInstancesPendingQC--;
                      break;
                    case IngestStatus.Rejected:
                      draft.countInstancesRejected--;
                      break;
                    case IngestStatus.WorkflowError:
                      draft.countInstancesError--;
                      break;
                    case IngestStatus.WaitingMezz:
                      draft.countInstancesWaitingMezz--;
                      break;
                    case IngestStatus.QcInProgress:
                      draft.countInstancesQcInProgress--;
                      break;
                    case IngestStatus.MissingMetadata:
                      draft.countInstancesMissingMetadata--;
                      break;
                  }
                })
              );
              break;
          }
        });
    });

    // Disconnect websocket
    return function cleanup() {
      eventsNotifier.Disconnect().then(function () {});
    };
  }, []);

  const additionalFilters = (
    <>
      {isCockpit && (
        <Select
          displayEmpty
          IconComponent={ExpandMoreRoundedIcon}
          value={filterPlatform}
          inputProps={{ "aria-label": "Without label" }}
          MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
          startAdornment={<PlaceIcon />}
          onChange={(value) => handleFilterPlatform(value)}
        >
          <MenuItem value={-1}>{t("common:platform")}</MenuItem>
          {platforms!.map((platform, index) => (
            <MenuItem value={platform.id!} key={index}>
              {platform.name}
            </MenuItem>
          ))}
        </Select>
      )}
      {/* <Select
        displayEmpty
        IconComponent={ExpandMoreRoundedIcon}
        startAdornment={<PersonOutlineOutlinedIcon />}
        inputProps={{ "aria-label": "Without label" }}
        MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
        value={createdByFilter}
        onChange={handleChangeCreatedBySelect}
      >
        <MenuItem value="" selected>
          {t("common:createdBy")}
        </MenuItem>
        {filtersData.createdByNames.length > 0 &&
          filtersData.createdByNames.map((userName) => (
            <MenuItem key={userName} value={userName}>
              {userName}
            </MenuItem>
          ))}
      </Select> */}
      <Select
        displayEmpty
        IconComponent={ExpandMoreRoundedIcon}
        startAdornment={<ManageAccountsOutlinedIcon />}
        inputProps={{ "aria-label": "Without label" }}
        MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
        value={providerFilter}
        onChange={handleChangeProviderSelect}
      >
        <MenuItem value="" selected>
          {t("common:provider")}
        </MenuItem>
        {filtersData.providersNames.length > 0 &&
          filtersData.providersNames.map((providerName) => (
            <MenuItem key={providerName} value={providerName}>
              {providerName}
            </MenuItem>
          ))}
      </Select>
      <Select
        displayEmpty
        IconComponent={ExpandMoreRoundedIcon}
        startAdornment={<OutlinedFlagOutlinedIcon />}
        inputProps={{ "aria-label": "Without label" }}
        MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
        value={priorityFilter}
        onChange={handleChangePrioritySelect}
      >
        <MenuItem value="">{t("menu:item.priorityLevel")}</MenuItem>
        <MenuItem value="High">{t("common:high")}</MenuItem>
        <MenuItem value="Normal">{t("common:normal")}</MenuItem>
        <MenuItem value="Low">{t("common:low")}</MenuItem>
      </Select>
      <Select
        displayEmpty
        IconComponent={ExpandMoreRoundedIcon}
        startAdornment={<TokenOutlined />}
        inputProps={{ "aria-label": "Without label" }}
        MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
        value={typeFilter}
        onChange={handleFilterType}
      >
        <MenuItem value="" selected>
          {t("common:type")}
        </MenuItem>
        {rows
          .map((x) => x.assetType)
          .filter(Distinct)
          .map((type) => (
            <MenuItem key={type} value={type}>
              {type}
            </MenuItem>
          ))}
      </Select>
      <Select
        displayEmpty
        IconComponent={ExpandMoreRoundedIcon}
        startAdornment={<VerifiedOutlined />}
        inputProps={{ "aria-label": "Without label" }}
        MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
        value={classFilter}
        onChange={handleFilterClass}
      >
        <MenuItem value="" selected>
          {t("common:class")}
        </MenuItem>
        {rows
          .map((x) => x.assetClass)
          .filter(Distinct)
          .map((type) => (
            <MenuItem key={type} value={type}>
              {type}
            </MenuItem>
          ))}
      </Select>
      {/* <Select
        IconComponent={ExpandMoreRoundedIcon}
        startAdornment={<ListIcon />}
        displayEmpty
        renderValue={(v) => {
          if (v.length === 0) return "Episodes";
          else return "EP. " + v.map((value) => value.toString()).join(", ");
        }}
        input={<OutlinedInput />}
        MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
        multiple
        value={episodesFilter}
        onChange={handleFilterEpisodes}
      >
        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((type) => (
          <MenuItem key={type} value={type}>
            {type}
          </MenuItem>
        ))}
      </Select> */}
    </>
  );

  return (
    <>
      {!isCockpit && (
        <Grid2 container spacing={1} sx={{ mb: 2, pt: 2 }}>
          <Grid2 size={xsValue}>
            <PageFilter
              variant="success"
              title={countInstances.countInstancesAccepted}
              subtitle={t("common:accepted")}
              icon={<TaskAltOutlinedIcon />}
              active={statusFilter === IngestStatus.Accepted}
              onClick={() => changeStatusFilter(IngestStatus.Accepted)}
            />
          </Grid2>
          <Grid2 size={xsValue}>
            <PageFilter
              variant="primary"
              title={countInstances.countInstancesProcessing}
              subtitle={t("common:processing")}
              icon={<CachedIcon />}
              active={statusFilter === IngestStatus.Running}
              onClick={() => changeStatusFilter(IngestStatus.Running)}
            />
          </Grid2>
          <Grid2 size={xsValue}>
            <PageFilter
              variant="warning"
              title={countInstances.countInstancesPendingQC}
              subtitle={t("common:pendingQc")}
              icon={<RemoveRedEyeOutlinedIcon />}
              active={statusFilter === IngestStatus.PendingQC}
              onClick={() => changeStatusFilter(IngestStatus.PendingQC)}
            />
          </Grid2>
          {!isDrive && (
            <Grid2 size={xsValue}>
              <PageFilter
                variant="warning"
                title={countInstances.countInstancesMissingMetadata}
                subtitle={t("common:missingMetadata")}
                icon={<ReportProblemOutlinedIcon />}
                active={statusFilter === IngestStatus.MissingMetadata}
                onClick={() => changeStatusFilter(IngestStatus.MissingMetadata)}
              />
            </Grid2>
          )}
          {!isDrive && (
            <Grid2 size={xsValue}>
              <PageFilter
                variant="info"
                title={countInstances.countInstancesWaitingMezz}
                subtitle={t("common:waitingForMezz")}
                icon={<HourglassEmptyIcon />}
                active={statusFilter === IngestStatus.WaitingMezz}
                onClick={() => changeStatusFilter(IngestStatus.WaitingMezz)}
              />
            </Grid2>
          )}
          <Grid2 size={xsValue}>
            <PageFilter
              variant="error"
              title={countInstances.countInstancesRejected}
              subtitle={t("common:rejected")}
              icon={<CancelOutlinedIcon />}
              active={statusFilter === IngestStatus.Rejected}
              onClick={() => changeStatusFilter(IngestStatus.Rejected)}
            />
          </Grid2>
          <Grid2 size={xsValue}>
            <PageFilter
              variant="error"
              title={countInstances.countInstancesError}
              subtitle={t("common:upperCaseError")}
              icon={<ErrorOutlineOutlinedIcon />}
              active={statusFilter === IngestStatus.WorkflowError}
              onClick={() => changeStatusFilter(IngestStatus.WorkflowError)}
            />
          </Grid2>
        </Grid2>
      )}

      <DataTable
        loading={isLoading}
        columns={plaformMode === PlatformMode.Adam ? [adamCols, ...ingestProcessingColumns] : ingestProcessingColumns}
        rows={filteredRows}
        tableKey={location.pathname.includes("sources") ? TableUuids.ADAM_SOURCES : TableUuids.INGEST_WORKFLOW_TABLE}
        apiRef={apiGrid}
        disableRowSelectionOnClick
        getRowHeight={() => "auto"}
        pagination={true}
        additionalFilters={additionalFilters}
        additionalActions={
          <>
            {UserHasAcl(AclTypes.aclIngestPlaceholderCreate) && (
              <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={showNewRequestPanel}>
                {t("button:newRequest")}
              </Button>
            )}
          </>
        }
        initialColumnVisibility={{
          platformId: platforms && platforms.length > 0 ? true : false,
          assetParentTitleId: !isDrive,
          annotation: false,
        }}
        onRowClick={showAssetPanel}
      />

      <AssetPanel
        open={openAssetPanel}
        onClose={hideAssetPanel}
        closePanel={hideAssetPanel}
        assetId={selectedAssetId}
        reload={selectedAssetId}
      />
      <NewRequestPanel type="create" open={openNewRequestPanel} onClose={hideNewRequestPanel} />
    </>
  );
};

export default IngestWorkflowTable;
